import { createRef, Component } from 'react'
import * as styles from 'styles/molecules/forms/OnboardingForm.css'
import { connect, MapDispatchToPropsFunction } from 'react-redux'
import { Field, FieldValue, change, formValueSelector, arrayRemove } from 'redux-form'
import { FORM_ONBOARDING_PROPERTY } from 'constants/forms'
import { HomePrice, HomeFormValues } from 'interfaces/property'
import { State } from 'interfaces/state'
import { isFieldArrayFieldActive, parseValueToBoolean } from 'helpers/formHelpers'
import { getHomePriceKey } from 'helpers/propertyHelpers'
import { PropertyListChildProps } from 'components/molecules/forms/PropertyList'
import TextButton from 'components/atoms/buttons/TextButton'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import CheckboxWrapped from 'components/atoms/inputs/CheckboxWrapped'
import CityStateField from 'components/atoms/inputs/CityStateField'
import SelectWrapped from 'components/atoms/inputs/OldSelectWrapped'
import HomePriceBalloon from 'components/atoms/balloons/HomePriceBalloon'
import {
  FIELD_ADDRESS1_PROPS,
  FIELD_ADDRESS2_PROPS,
  FIELD_ZIP_PROPS,
  FIELD_PURCHASED_PROPS,
  FIELD_HOME_ESTIMATED_PROPS,
  FIELD_PRIMARY_RESIDENCE_PROPS,
  FIELD_CURRENCY_WRAPPED_PROPS,
} from 'constants/fields'
import { smoothScrollInfoView } from 'helpers/styleHelpers'
import BalloonWrapper from 'components/atoms/balloons/BalloonWrapper'

type HomesFormFieldsStateProps = {
  price: HomePrice | null
  allValues: HomeFormValues[]
  isValueFieldActive: boolean
  isAddress1FieldActive: boolean
  canBeRemoved: boolean
  addressValue: string
}

type HomesFormFieldsDispatchProps = {
  changeFormValue: (name: string, value: FieldValue) => void
  remove: () => void
}

type HomesFormFieldsOwnProps = PropertyListChildProps

type HomesFormFieldsProps = HomesFormFieldsStateProps & HomesFormFieldsDispatchProps & HomesFormFieldsOwnProps

class HomesFormFields extends Component<HomesFormFieldsProps, {}> {
  private rootRef = createRef<HTMLDivElement>()

  render () {
    const {
      name, index, allValues, changeFormValue, canBeRemoved,
      remove, isValueFieldActive, price, addressValue,
    } = this.props

    const setPrimaryResidence = () => {
      allValues.forEach((home: HomeFormValues, idx: number) => {
        if (home.is_primary_residence && index !== idx) {
          changeFormValue(`homes[${ idx }].is_primary_residence`, false)
        }
      })
    }

    const priceBalloon = isValueFieldActive && price && addressValue ? (
      <BalloonWrapper>
        <HomePriceBalloon price={ price } addressValue={ addressValue } />
      </BalloonWrapper>
    ) : null

    return (
      <div ref={ this.rootRef }>
        <div className={ styles.innerHeader }>Home #{ index + 1 }
          { canBeRemoved && (
            <TextButton onClick={ remove }>
              Delete
            </TextButton>
          )}
        </div>
        <br />
        <ul className={ styles.controlList }>
          <li className={ styles.controlListItem }>
            <Field
              name={ `${ name }.address1` }
              component={ TextFieldWrapped }
              props={{
                ...FIELD_ADDRESS1_PROPS,
                theme: 'snow',
              }}
            />
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name={ `${ name }.address2` }
              component={ TextFieldWrapped }
              props={{
                ...FIELD_ADDRESS2_PROPS,
                theme: 'snow',
              }}
            />
          </li>
          <li className={ styles.controlListItem }>
            <CityStateField theme='snow' name={ `${ name }.city` } />
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name={ `${ name }.zip5` }
              props={ FIELD_ZIP_PROPS }
              component={ TextFieldWrapped }
            />
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name={ `${ name }.purchased_at` }
              props={{
                ...FIELD_PURCHASED_PROPS,
                theme: 'snow',
                qaMarker: 'home-purchased-at-input',
              }}
              component={ SelectWrapped }
            />
          </li>
          <li className={ styles.controlListItem }>
            <Field
              name={ `${ name }.estimated_value` }
              props={ FIELD_HOME_ESTIMATED_PROPS }
              onFocus={ () => this.setState({ showBalloon: true }) }
              component={ TextFieldWrapped }
              { ...FIELD_CURRENCY_WRAPPED_PROPS }
            />
            { priceBalloon }
          </li>
          <li className={ styles.rowButtonListItem }>
            <Field
              name={ `${ name }.is_primary_residence` }
              props={{ ...FIELD_PRIMARY_RESIDENCE_PROPS, onValueChange: setPrimaryResidence }}
              component={ CheckboxWrapped }
              parse={ parseValueToBoolean }
            />
          </li>
          <li className={ styles.controlListItem } />
        </ul>
      </div>
    )
  }

  componentDidMount () {
    const rootNode = this.rootRef.current
    if (this.props.isAddress1FieldActive) {
      smoothScrollInfoView(rootNode)
    }
  }
}

const selector = formValueSelector(FORM_ONBOARDING_PROPERTY)

const HOMES_PROPERTY_NAME = 'homes'
const mapStateToProps = (state: State, ownProps: HomesFormFieldsOwnProps): HomesFormFieldsStateProps => {
  const { index } = ownProps
  const form = state.form[FORM_ONBOARDING_PROPERTY]

  const allValues = selector(state, 'homes') as HomeFormValues[]
  const canBeRemoved = allValues.length > 1

  const values = allValues[index]
  const { homePrices } = state.data.property
  const priceKey = getHomePriceKey(values)
  const price = !priceKey || !(priceKey in homePrices) ? null : homePrices[priceKey]

  const activeField = form && form.active ? form.active : ''
  const isValueFieldActive = isFieldArrayFieldActive(HOMES_PROPERTY_NAME, index, 'estimated_value', activeField)
  const isAddress1FieldActive = isFieldArrayFieldActive(HOMES_PROPERTY_NAME, index, 'address1', activeField)
  const addressValue = values.address1

  return {
    price,
    allValues,
    isValueFieldActive,
    isAddress1FieldActive,
    canBeRemoved,
    addressValue,
  }
}

const mapDispatchToProps: MapDispatchToPropsFunction<HomesFormFieldsDispatchProps, HomesFormFieldsOwnProps> = (dispatch, ownProps) => ({
  changeFormValue: (name: string, value: any) => dispatch(change(FORM_ONBOARDING_PROPERTY, name, value)),
  remove: () => dispatch(arrayRemove(FORM_ONBOARDING_PROPERTY, 'homes', ownProps.index)),
})

const connected = connect<
  HomesFormFieldsStateProps,
  HomesFormFieldsDispatchProps,
  HomesFormFieldsOwnProps
>(mapStateToProps, mapDispatchToProps)(HomesFormFields)
export default connected
